import React, { useMemo, useState } from "react";
import { Check, Monitor, RotateCcw, Save, Smartphone, Tablet, X } from "lucide-react";

import { cloneDefaultPublicChatLayout, loadPublicChatLayout, savePublicChatLayout } from "@/lib/publicChatLayout";

const CONTROL_GROUPS = [
  {
    title: "Header",
    items: [
      ["headerHeight", "Height", 56, 120],
      ["headerX", "Side padding", 0, 40],
      ["headerY", "Vertical padding", 0, 28],
    ],
  },
  {
    title: "Messages",
    items: [
      ["messagesY", "Room vertical padding", 0, 40],
      ["contentX", "Conversation side padding", 0, 40],
      ["contentY", "Conversation vertical padding", 0, 40],
      ["messageGap", "Message spacing", 0, 32],
      ["maxWidth", "Conversation max width", 320, 1400],
    ],
  },
  {
    title: "Composer",
    items: [
      ["composerX", "Outer side padding", 0, 32],
      ["composerY", "Outer vertical padding", 0, 28],
      ["composerInnerX", "Input side padding", 0, 18],
      ["composerInnerY", "Input vertical padding", 0, 18],
    ],
  },
];

function Control({ label, value, min, max, onChange }) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between gap-3 text-[11px] font-semibold text-slate-500">
        <span>{label}</span>
        <span className="tabular-nums text-slate-900">{value}px</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-grab accent-pink-500 active:cursor-grabbing"
      />
    </label>
  );
}

export default function PublicChatDesigner() {
  const [layout, setLayout] = useState(() => loadPublicChatLayout());
  const [device, setDevice] = useState("desktop");
  const [saved, setSaved] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  const active = layout[device];
  const deviceLabel = useMemo(() => device === "desktop" ? "Desktop" : "Mobile", [device]);

  const update = (key, value) => {
    setSaved(false);
    setLayout((current) => {
      const next = { ...current, [device]: { ...current[device], [key]: value } };
      savePublicChatLayout(next);
      return next;
    });
    setPreviewKey((key) => key + 1);
  };

  const reset = () => {
    const defaults = cloneDefaultPublicChatLayout();
    setLayout(defaults);
    savePublicChatLayout(defaults);
    setPreviewKey((key) => key + 1);
    setSaved(false);
  };

  const save = () => {
    savePublicChatLayout(layout);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1800);
  };

  return (
    <div className="min-h-screen">
      <div className="flex min-h-screen items-center justify-center bg-slate-200/80 p-4 dark:bg-slate-900">
        <div
          className={`relative overflow-hidden bg-black shadow-2xl ${device === "mobile" ? "h-[844px] w-[390px] rounded-[38px] border-[8px] border-slate-900" : "h-[760px] w-[min(1100px,calc(100vw-390px))] rounded-2xl border border-slate-300"}`}
        >
          <iframe
            key={device + "-" + previewKey}
            title={`Yaprio Public Chat ${device} preview`}
            src={`/public-chat?designPreview=${device}`}
            className="h-full w-full border-0 bg-white"
          />
        </div>
      </div>

      <aside className="fixed right-4 top-20 z-[100] flex max-h-[calc(100vh-6rem)] w-[330px] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-950/95 max-[640px]:bottom-20 max-[640px]:left-2 max-[640px]:right-2 max-[640px]:top-auto max-[640px]:w-auto max-[640px]:max-h-[48vh]">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <div>
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Public Chat Designer</p>
            <p className="text-[10px] font-medium text-slate-400">Editing the real Yaprio chat</p>
          </div>
          <button
            type="button"
            onClick={() => window.location.assign("/public-chat")}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
            aria-label="Close designer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 border-b border-slate-100 p-3 dark:border-slate-800">
          <button
            type="button"
            onClick={() => setDevice("desktop")}
            className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${device === "desktop" ? "bg-pink-500 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300"}`}
          >
            <Monitor className="h-4 w-4" /> Desktop
          </button>
          <button
            type="button"
            onClick={() => setDevice("mobile")}
            className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${device === "mobile" ? "bg-pink-500 text-white" : "bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300"}`}
          >
            <Smartphone className="h-4 w-4" /> Mobile
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-4 rounded-2xl border border-pink-100 bg-pink-50/70 p-3 dark:border-pink-900/50 dark:bg-pink-950/20">
            <p className="text-xs font-bold text-pink-600 dark:text-pink-300">{deviceLabel} preview</p>
            <p className="mt-1 text-[10px] leading-4 text-pink-500/80">
              Drag the sliders to change spacing live. The preview underneath is the actual Public Chat with real messages and controls.
            </p>
          </div>

          {CONTROL_GROUPS.map((group) => (
            <section key={group.title} className="mb-5">
              <h3 className="mb-3 text-xs font-extrabold uppercase tracking-wider text-slate-900 dark:text-white">{group.title}</h3>
              <div className="space-y-4">
                {group.items.map(([key, label, min, max]) => (
                  <Control
                    key={key}
                    label={label}
                    value={active[key]}
                    min={min}
                    max={max}
                    onChange={(value) => update(key, value)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-slate-100 p-3 dark:border-slate-800">
          <button
            type="button"
            onClick={reset}
            className="flex items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-300"
          >
            <RotateCcw className="h-4 w-4" /> Reset
          </button>
          <button
            type="button"
            onClick={save}
            className="flex items-center justify-center gap-2 rounded-xl bg-pink-500 px-3 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-pink-600"
          >
            {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            {saved ? "Saved" : "Save"}
          </button>
        </div>
        <div className="border-t border-slate-100 px-4 py-2 text-center text-[9px] text-slate-400 dark:border-slate-800">
          Saved to this browser. Refresh Public Chat to apply saved layout outside the designer.
        </div>
      </aside>
    </div>
  );
}
